import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import wx from 'weixin-js-sdk';
import { getAdviserProfilePlus } from '../../store/adviserProfilePlus/duck';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import { INIT, SUCCESS } from '../../constants/phase';
import AdviserInfoPage from './AdviserInfoPage';
import { usePrevious } from '../../utils/hooks';
import { getSearchObject } from '../../utils/router';
import { getWhatsappUrl } from '../../utils/whatsapp';
import { getVCardStringWithPhoto } from '../../utils/vcard';
import { download } from '../../utils/download';
import ECardWelcomeDialog from './ECardWelcomeDialog/ECardWelcomeDialog';
import { getReferralLink } from '../../utils/referral';
import { REFERRAL_SOURCE } from '../../constants/referral';
import { Capacitor } from '@capacitor/core';
import ECardWeChatDialog from './ECardWeChatDialog/ECardWeChatDialog';

const AdviserInfoPagePublic = props => {
  const {
    intl,
    aid,
    location,
    match,
    showPadding,
    userDetails,
    adviserInfo,
    getAdviserProfilePlusPhase,
    getAdviserProfilePlus
  } = props;

  const [eCardWelcomeDialogOpen, setECardWelcomeDialogOpen] = useState(false);
  const [triggerFormOpen, setTriggerFormOpen] = useState(false);
  const [wechatQrCodeDialogOpen, setWechatQrCodeDialogOpen] = useState(false);
  const [defaultMessage, setDefaultMessage] = useState(undefined);
  const prevGetAdviserProfilePlusPhase = usePrevious(getAdviserProfilePlusPhase);
  const autoDownloaded = useRef(false);
  const userId = aid ?? _.get(match, 'params.userId');

  function convertToPlainText(jsonString) {
    try {
      const json = JSON.parse(jsonString);
      return json.blocks.map(block => block.text).join(' ');
    } catch {
      return '';
    }
  }

  useEffect(() => {
    if (getAdviserProfilePlusPhase === INIT || userId !== adviserInfo?._id) {
      getAdviserProfilePlus(userId, location);
    }
  }, [getAdviserProfilePlus, getAdviserProfilePlusPhase, userId, location]);

  useEffect(() => {
    if (getAdviserProfilePlusPhase !== prevGetAdviserProfilePlusPhase) {
      if (getAdviserProfilePlusPhase === SUCCESS) {
        const searchObj = getSearchObject({ location });
        const { defaultMessage, fromQRCode, vcf, d, s } = searchObj || {};
        const { name, whatsapp, adviserMessage, avatar } = adviserInfo || {};
        const messageMapping = { a: 'medical-reserve-default-msg' };

        if ((defaultMessage || d)) {
          const text = (
            defaultMessage ||
            (messageMapping[d] && intl.formatMessage({ id: messageMapping[d] })) ||
            ''
          ).replace('*adviserName*', name);
          setDefaultMessage(text);
          if (
            (whatsapp || adviserInfo?.wechatUrl || adviserInfo?.wechatId) &&
            (s === REFERRAL_SOURCE.LAZY_PACK ||
              s === REFERRAL_SOURCE.PDF ||
              s === REFERRAL_SOURCE.HOT_TOPIC_STUDIO ||
              s === REFERRAL_SOURCE.CUSTOM_NEWSFEED)
          ) {
            setECardWelcomeDialogOpen({ phone: whatsapp, text });
          } else {
            setTriggerFormOpen(true);
          }
        } else {
          if (fromQRCode) {
            setTriggerFormOpen(true);
          }
        }

        if (vcf) {
          if (!autoDownloaded.current) {
            autoDownloaded.current = true;
            getVCardStringWithPhoto(adviserInfo, string => {
              download(string, `${name}.vcf`, 'text/x-vcard');
            });
          }
        }

        wx.ready(function () {
          wx.updateAppMessageShareData({
            title: name,
            desc: convertToPlainText(adviserMessage || ""),
            link: getReferralLink({ targetAdviser: adviserInfo }),
            imgUrl: avatar && avatar.includes("https") ? avatar : null
          });
        });
      }
    }
  }, [getAdviserProfilePlusPhase, prevGetAdviserProfilePlusPhase, location, adviserInfo]);

  const isLoggedIn = userDetails && userDetails._id;

  const onECardWelcomeDialogSayNo = () => {
    setECardWelcomeDialogOpen(false);
    setTimeout(() => {
      return setTriggerFormOpen(true);
    }, 150);
  };

  const onECardWelcomeDialogSayYes = () => {
    const { phone, text } = eCardWelcomeDialogOpen || {};
    const whatsappUrl = getWhatsappUrl({ phone, text });
    window.open(whatsappUrl, '_blank');
    setECardWelcomeDialogOpen(false);
  };

  const isMobileBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iphone|ipad|ipod|mobile/i.test(userAgent) && !Capacitor.isNativePlatform();
  };

  const goWhatsapp = () => {
    window.open(
      `https://api.whatsapp.com/send?phone=${adviserInfo?.whatsapp.replace('+', '')}&text=${encodeURIComponent(defaultMessage)}`,
      '_blank'
    );
  };

  const goWechat = () => {
    if (Capacitor.isNativePlatform() || isMobileBrowser()) {
      let wechatMiniProgramUrl = `weixin://dl/business/?appid=wxd066ac768dfb3ef8&path=pages/qrCode/qrCode&query=userId%3D${adviserInfo?._id
        }&env_version=${process.env.STAGE === 'production' ? 'release' : 'trial'}`;
      window.open(wechatMiniProgramUrl, '_blank');
    } else {
      setWechatQrCodeDialogOpen(true);
    }
  };

  return (
    <div key={location.pathname} style={{ height: '100%', paddingTop: 0, paddingBottom: 0 }}>
      <ECardWeChatDialog intl={intl} open={wechatQrCodeDialogOpen} onClose={() => setWechatQrCodeDialogOpen(false)} adviserInfo={adviserInfo} wechatId={adviserInfo?.wechatId} wechatUrl={adviserInfo?.wechatUrl} />
      <ECardWelcomeDialog
        adviserInfo={adviserInfo}
        availableContactMethods={[
          { type: 'whatsapp', value: adviserInfo?.whatsapp, onClick: goWhatsapp },
          { type: 'wechat', value: (adviserInfo?.wechatId !== "" || adviserInfo?.wechatUrl !== "") ? true : null, onClick: goWechat },
        ]}
        open={!!eCardWelcomeDialogOpen && (adviserInfo?.whatsapp !== "" || adviserInfo?.wechatId !== "" || adviserInfo?.wechatUrl !== "")}
        onSayNo={onECardWelcomeDialogSayNo}
        onSayYes={onECardWelcomeDialogSayYes}
        onClose={() => setECardWelcomeDialogOpen(false)}
      />
      <AdviserInfoPage withNav={isLoggedIn} triggerFormOpen={triggerFormOpen} defaultMessage={defaultMessage} />
    </div>
  );
};

const container = connect(
  state => ({
    userDetails: state.user.userDetails,
    adviserInfo: _.get(state.adviserProfilePlus, 'adviserProfilePlus.adviserInfo'),
    getAdviserProfilePlusPhase: state.adviserProfilePlus.getAdviserProfilePlusPhase,
    showPadding: state.app.get('showPadding')
  }),
  {
    getAdviserProfilePlus
  }
)(AdviserInfoPagePublic);

export default injectIntl(container);
